import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, Mail, MapPin, MessageCircle, Phone, Sprout, Globe, Contact as ContactIcon } from "lucide-react";
import axios, { API } from "../lib/api";
import useLive from "../hooks/useLive";
import useLenis from "../hooks/useLenis";
import { useLang } from "../lib/i18n";
import { Nav } from "../components/site/Nav";
import { Footer } from "../components/site/Footer";
import { OrderDialog } from "../components/site/OrderDialog";
import { ChatWidget } from "../components/site/ChatWidget";
import { MaskLines, Reveal } from "../components/site/Reveal";
import { ClosuresNotice } from "../components/site/ClosuresNotice";

const InstagramMark = ({ className = "" }) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="none"><defs><linearGradient id="kgf-contact-instagram-gradient" x1="2" y1="22" x2="22" y2="2" gradientUnits="userSpaceOnUse"><stop stopColor="#FFB13D"/><stop offset=".48" stopColor="#F02D7D"/><stop offset="1" stopColor="#8B35D8"/></linearGradient></defs><rect x="3" y="3" width="18" height="18" rx="5.2" stroke="url(#kgf-contact-instagram-gradient)" strokeWidth="2.2"/><circle cx="12" cy="12" r="4.1" stroke="url(#kgf-contact-instagram-gradient)" strokeWidth="2.2"/><circle cx="17.7" cy="6.5" r="1.2" fill="#F02D7D"/></svg>
);
const FacebookMark = ({ className = "" }) => (<svg aria-hidden="true" viewBox="0 0 24 24" className={className}><circle cx="12" cy="12" r="10" fill="#1877F2"/><path fill="#fff" d="M13.35 20v-7.3h2.45l.37-2.85h-2.82V8.03c0-.83.23-1.4 1.42-1.4h1.51V4.08c-.26-.04-1.16-.11-2.2-.11-2.18 0-3.67 1.33-3.67 3.77v2.1H7.95v2.85h2.46V20h2.94Z"/></svg>);

const Item = ({ icon: Icon, label, value, href, testId, soon, hideEmpty }) => hideEmpty && !value ? null : (
  <div data-testid={testId} className="group border-t hairline py-6 grid grid-cols-[auto_1fr] gap-5">
    <span className="grid h-11 w-11 place-items-center rounded-full bg-[#0F8A78]/20 text-[var(--mint)] transition-transform group-hover:-rotate-6"><Icon className="h-5 w-5" /></span>
    <div>
      <p className="eyebrow mb-1.5">{label}</p>
      {value ? (
        href ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="text-lg sm:text-xl hover:text-[var(--husk)] transition-colors break-words">{value}</a>
          : <p className="text-lg sm:text-xl whitespace-pre-line">{value}</p>
      ) : <p className="text-lg text-[var(--sage)]">{soon}</p>}
    </div>
  </div>
);

export default function Contact() {
  useLenis();
  const { t, lang } = useLang();
  const { products, closures } = useLive(30000);
  const [c, setC] = useState(null);
  const [order, setOrder] = useState({ open: false, slug: null, prefill: null });
  const openOrder = (slug = null) => setOrder({ open: true, slug: typeof slug === "string" ? slug : null, prefill: null });

  useEffect(() => {
    axios.get(`${API}/contact`).then((r) => setC(r.data ?? null)).catch(() => setC(null));
  }, []);

  const phone = c?.phone || "+91 89292 68615";
  const whatsapp = c?.whatsapp || "+91 89292 68615";
  const email = c?.email || "info@kashyapgreenfuels.com";
  const q = encodeURIComponent(c?.map_query || c?.address || "");
  const wa = whatsapp.replace(/[^0-9]/g, "");
  const soon = t("contact.soon");

  return (
    <div className="grain relative" data-testid="contact-page">
      <Nav onOrder={openOrder} />
      <main className="pt-[120px] pb-24">
        <section className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-5">{t("contact.eyebrow")}</p>
            <h1 key={lang} className="font-display font-bold tracking-[-0.04em] leading-[0.92] text-6xl sm:text-7xl lg:text-8xl">
              <MaskLines lines={[t("contact.h1"), <span key="a" className="font-serif-i font-normal text-[var(--husk)]">{t("contact.accent")}</span>]} delay={0.2} />
            </h1>
            <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }} className="mt-8 max-w-md text-[var(--sage)] text-base sm:text-lg leading-relaxed">
              {t("contact.sub")}
            </motion.p>
            <div className="mt-10">
              <Item icon={MapPin} label={t("contact.address")} value={c?.address} testId="contact-address" soon={soon} />
              <Item icon={Phone} label={t("contact.phone")} value={phone} href={`tel:${phone.replace(/[^0-9+]/g, "")}`} testId="contact-phone" soon={soon} />
              <Item icon={MessageCircle} label={t("contact.whatsapp")} value={whatsapp} href={`https://wa.me/${wa}`} testId="contact-whatsapp" soon={soon} />
              <Item icon={Mail} label={t("contact.email")} value={email} href={`mailto:${email}`} testId="contact-email" soon={soon} />
              <Item icon={Clock} label={t("contact.hours")} value={c?.hours} testId="contact-hours" soon={soon} />
              {closures.length > 0 && <div className="pt-2"><ClosuresNotice closures={closures} compact testId="contact-closures-notice" /></div>}
            </div>
            <div className="mt-8 flex flex-wrap gap-3" aria-label="Social media links">
              <a data-testid="contact-instagram" href="https://www.instagram.com/kashyapgreenfuels/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border hairline px-5 py-3 hover:bg-white/5 transition-colors"><InstagramMark className="h-4 w-4 shrink-0" />Instagram</a>
              <a data-testid="contact-facebook" href="https://www.facebook.com/share/1DizBtxyEW/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border hairline px-5 py-3 hover:bg-white/5 transition-colors"><FacebookMark className="h-5 w-5 shrink-0" />Facebook</a>
              <a data-testid="contact-website" href="https://kashyapgreenfuels.com/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border hairline px-5 py-3 hover:bg-white/5 transition-colors"><Globe className="h-4 w-4 text-[#38BDF8]" />Website</a>
              <a data-testid="contact-page-link" href="/contact" className="inline-flex items-center gap-2 rounded-full border hairline px-5 py-3 hover:bg-white/5 transition-colors"><ContactIcon className="h-4 w-4 text-[#D7A64B]" />Contact</a>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="contact-order-btn" onClick={() => openOrder()} className="group inline-flex items-center gap-2 rounded-full bg-[var(--husk)] text-[var(--forest)] px-6 py-3.5 font-semibold transition-transform hover:-translate-y-0.5">
                {t("cta.order")} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
              </button>
              <a data-testid="contact-supply-link" href="/#farmers" className="inline-flex items-center gap-2 rounded-full border hairline px-6 py-3.5 hover:bg-white/5 transition-colors">
                <Sprout className="h-4 w-4" /> {t("contact.supply")}
              </a>
            </div>
          </div>
          <Reveal className="lg:col-span-7">
            <div className="relative overflow-hidden rounded-[32px] ring-1 ring-[#0F8A78]/40 h-[420px] sm:h-[560px] lg:h-[calc(100svh-180px)] lg:sticky lg:top-[100px] bg-[var(--forest-2)]">
              {q && (
                <iframe
                  data-testid="contact-map"
                  title={t("contact.mapTitle")}
                  src={`https://www.google.com/maps?q=${q}&output=embed`}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="absolute inset-0 h-full w-full border-0 [filter:invert(0.92)_hue-rotate(180deg)_saturate(0.6)_brightness(0.95)]"
                />
              )}
              {q && (
                <a
                  data-testid="contact-directions-btn"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${q}`}
                  target="_blank"
                  rel="noreferrer"
                  className="group absolute bottom-5 left-5 inline-flex items-center gap-2 rounded-full bg-[var(--cream)] text-[var(--forest)] px-5 py-3 font-semibold shadow-xl transition-transform hover:-translate-y-0.5"
                >
                  {t("contact.directions")} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
                </a>
              )}
            </div>
          </Reveal>
        </section>
      </main>
      <Footer onOrder={openOrder} />
      <OrderDialog open={order.open} onOpenChange={(v) => setOrder((o) => ({ ...o, open: v }))} products={products} initialSlug={order.slug} prefill={order.prefill} closures={closures} />
      <ChatWidget onDraft={(d) => setOrder({ open: true, slug: products.some((p) => p.slug === d?.product_slug) ? d.product_slug : null, prefill: d })} />
    </div>
  );
}
